<template>
  <b-overlay :show="loading">
    <div class="pt-2 pb-2 pl-2 border card shadow max-width-800" style="border-radius:10px; font-weight: 600;">
      <div class="pb-1">
        Report - Lab
      </div>

      <b-row>
        <b-col md="2">
          <b-form-datepicker
            :value="dateStart"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="From"
            @input="(val) => dateStart = val"
          />
        </b-col>
        <b-col md="2">
          <b-form-datepicker
            :value="dateEnd"
            :close-button="true"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            reset-button
            placeholder="To"
            :min="dateStart"
            @input="(val) => dateEnd = val"
          />
        </b-col>

        <b-col md="2">
          <v-select
            class="filter-select"
            placeholder="Status"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :value="statusFilter"
            :options="statusOptions"
            :reduce="val => val.value"
            :disabled="loading"
            @input="(val) => statusFilter = val"
          />
        </b-col>

        <b-col md="2" class="pr-2">
          <lab-select
            :value="lab_id" 
            @change="val => lab_id = val"
          />
        </b-col>

        <b-col md="2" class="pr-2">
          <lab-category-filter-select
            :value="lab_category_id" 
            @change="val => lab_category_id = val"
          />
        </b-col>

        <b-col md="2" class="pr-2">
          <lab-type-select
            ref="labTypeSelect"
            :value="lab_type_id"
            @change="val => lab_type_id = val"
          />
        </b-col>
      </b-row>
    </div>

    <div>
      <b-row class="match-height">
        <b-col class="max-width-800">
          <report-pie-chart :title="'Lab Category'" :series="labCategoryChartSeries" :chart-options="labCategoryChartOptions"></report-pie-chart>
        </b-col>
      </b-row>
    </div>

    <div class="max-width-800">
      <list-table
        :records="records" 
        :columns="tableColumns"
        :current-page-number="1"
        :show-import-button="false"
        :show-export-button="can('main_lab_reports:CanExport')"
        :show-search="false"
        :total-records="totalRecords"
        @export="onExport"
      >
      </list-table>
    </div>

  </b-overlay>
</template>

<script>
import { debounce, get } from "lodash";

import { 
  BAlert, 
  BAvatar,
  BBadge, 
  BImg, 
  BCard, 
  BCol, 
  BLink, 
  BOverlay, 
  BRow, 
  BFormDatepicker, 
  BCardImg 
} from "bootstrap-vue"

import { startOfMonth } from 'date-fns'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import FileDownload from "js-file-download";
import ListTable from "@/@core/components/ListTable/ListTable.vue";

import LabSelect from "@core/components/shared/admin/selects/LabSelect.vue"
import LabCategoryFilterSelect from "@core/components/shared/admin/selects/LabCategoryFilterSelect.vue"
import LabTypeSelect from "@core/components/shared/admin/selects/LabTypeSelect.vue"
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ReportPieChart from "@core/components/shared/admin/reports/view/ReportPieChart.vue"

const watchHandler = {
  handler(){
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const watchHandlerAndResetPageNumber = {
  handler(){
    this.currentPage = 1
    this.debounceFetchListData()
    this.debounceFetchAllCharts()
  }, 
  immediate: false
}

const chartColors = [
  'rgba(7, 125, 188, 1)',
  'rgba(101, 163, 13, 1)',
  'rgba(7, 125, 188, 0.7)',
  'rgba(101, 163, 13, 0.7)',
  'rgba(75, 75, 75, 0.2)',
  'rgba(7, 125, 188, 0.6)',
  'rgba(101, 163, 13, 0.6)',
  'rgba(7, 125, 188, 0.4)',
  'rgba(101, 163, 13, 0.4)',
  'rgba(7, 125, 188, 0.2)'
]

export default {
  components: {
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    vSelect,
    BFormDatepicker,
    ReportPieChart,
    LabSelect,
    LabCategoryFilterSelect,
    LabTypeSelect,
    ListTable
  },
  directives: {
    Ripple,
  },
  data(){
    const monthBeginning = startOfMonth(new Date())
    return {
      loading: false,
      
      dateStart: this.formatDate(monthBeginning, 'yyyy-MM-dd'),
      dateEnd: this.formatDate(new Date(), 'yyyy-MM-dd'),
      statusFilter: "",
      lab_id: null,
      lab_category_id: null,
      lab_type_id: null,

      records: [],

      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Processing', value: 'processing' },
        { label: 'Processed', value: 'completed' },
      ],
      
      labCategoryChartSeries: [0, 0, 0],
      labCategoryChartOptions: {
        chart: {
          type: 'pie',
        },
        colors: ['rgba(7, 125, 188, 1)', 'rgba(101, 163, 13, 1)', 'rgba(7, 125, 188, 0.7)'],
      },

      tableColumns: [
        { key: 'lab', label: "Lab Facility", sortable: false, },
        { key: 'pending', label: "Number of Labs Pending", sortable: false, },
        { key: 'processed', label: "Number of Labs Processed", sortable: false, },
      ],
      
      debounceFetchListData: null,
      debounceFetchAllCharts: null,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debounceFetchListData()
      }, 
      immediate: false
    },
    lab_category_id: {
      handler(new_category_id) {
        this.currentPage = 1;
        this.debounceFetchListData();
        this.debounceFetchAllCharts();
        this.$refs.labTypeSelect.fetchLabTypes(new_category_id);
      },
      immediate: false
    },
    currentPage: watchHandler,
    dateStart: watchHandlerAndResetPageNumber,
    dateEnd: watchHandlerAndResetPageNumber,
    statusFilter: watchHandlerAndResetPageNumber,
    lab_id: watchHandlerAndResetPageNumber,
    lab_type_id: watchHandlerAndResetPageNumber,
  },
  created() {
    this.debounceFetchListData = debounce(this.fetchListData, 500);
    this.debounceFetchAllCharts = debounce(this.fetchAllCharts, 500);

    this.fetchListData()
    this.fetchAllCharts()

    const {
      from,
      to,
      page = 1,
      limit = this.perPage
    } = this.$route.query;

    this.dateStart = from;
    this.dateEnd = to;
    this.currentPage = +page;
    this.perPage = +limit;
  },
  methods: {
    async fetchListData() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          limit: this.perPage,
          page: this.currentPage,
          status: this.statusFilter,
          lab_id: this.lab_id,
          lab_category_id: this.lab_category_id,
          lab_type_id: this.lab_type_id,
        }
        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.labReports.list(query);

        const { data } = request.data;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async fetchAllCharts() {
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          status: this.statusFilter,
          lab_id: this.lab_id,
        }

        const response = await this.useJwt().mainService.labReports.getChart(query);
        const [ response_data ] = get(response, 'data.data')

        if (response_data){
          this.labCategoryChartSeries = Object.values(response_data)
          this.labCategoryChartOptions = {
            chart: {
              type: 'pie',
            },
            colors: chartColors,
            labels: Object.entries(response_data).map(([key, value]) => `${this.sentenceCase(key)} - ${value}`)
          };
        } else {
          this.labCategoryChartSeries = [0, 0, 0];
          this.labCategoryChartOptions = {
            chart: {
              type: 'pie',
            },
            colors: chartColors,
            labels: ['Default Category 1 - 0', 'Default Category 2 - 0', 'Default Category 3 - 0']
          };
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onExport(){
      try {
        this.loading = true;

        const query = {
          from: this.dateStart,
          to: this.dateEnd,
          status: this.statusFilter,
          lab_id: this.lab_id,
          lab_category_id: this.lab_category_id,
          lab_type_id: this.lab_type_id,
        }

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const response = await this.useJwt().mainService.labReports.export(query);
        FileDownload(response.data, "lab-facility-report.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    }, 
  }
}
</script>

<style scoped>

</style>

